<script setup>
import { useReportSummaryStore } from "@/stores/home/useReportSummary.store.js";
import V4StatCard from "./partials/V4StatCard.vue";
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";
import { computed } from "vue";
import V4CompletedCoursesModal from "@/components/home/my-progress/V4CompletedCoursesModal.vue";
import V4CourseInProgressModal from "@/components/home/my-progress/V4CourseInProgressModal.vue";
import V4AssignedCourseModal from "@/components/home/my-progress/V4AssignedCourseModal.vue";
import V4CertificatesEarnedModal from "@/components/home/my-progress/V4CertificatesEarnedModal.vue";
import V4CompletedContents from "./V4CompletedContents.vue";
import V4TeamProgress from "./team-progress/V4TeamProgress.vue";
import { UserGroupIcon, ChevronDownIcon } from "@heroicons/vue/outline";
import { useAuthStore } from "@/stores/auth.store.js";
import V4TeamMembers from "@/components/home/V4TeamMembers.vue";

const reportSummaryStore = useReportSummaryStore();

const authStore = useAuthStore();

const { loadingSummary } = storeToRefs(reportSummaryStore);

const showCompletedCoursesModal = ref(false);
const showInProgressCoursesModal = ref(false);
const showAssignedCoursesModal = ref(false);
const showCertificatesEarnedModal = ref(false);
const showTeamProgress = ref(false);

const hasViewTeamProgressPermission = computed(
  () =>
    authStore.isSuperAdmin ||
    authStore.isAccountManager ||
    authStore.isDealerManager ||
    authStore.isMultiStoreManager ||
    authStore.isTeamLeader ||
    authStore.isRegionManager
);

const reportSummaryCards = computed(
  () => reportSummaryStore.reportSummaryCards
);

const handleClickShowTeamProgress = () => {
  showTeamProgress.value = !showTeamProgress.value;
};

const handleFetchReportSummary = () => {
  reportSummaryStore.handleFetchReportSummary();
};

const handleClickCard = (key) => {
  switch (key) {
    case "courses_completed_count":
      showCompletedCoursesModal.value = true;
      break;
    case "courses_in_progress_count":
      showInProgressCoursesModal.value = true;
      break;
    case "total_assigned_count":
      showAssignedCoursesModal.value = true;
      break;
    case "total_certificates_count":
      showCertificatesEarnedModal.value = true;
      break;
  }
};

onMounted(() => {
  handleFetchReportSummary();
});
</script>
<template>
  <div
    class="rounded-lg bg-app-white border border-untitled-gray-200 p-4 sm:p-5"
  >
    <div class="flex items-center gap-2 mb-4">
      <div
        class="flex items-center justify-center w-8 h-8 rounded-lg bg-app-primary/10 text-app-primary shrink-0"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-4 h-4"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
          />
        </svg>
      </div>
      <div>
        <h3 class="text-sm sm:text-base font-semibold text-untitled-gray-900">
          Your Progress at a Glance
        </h3>
        <p class="text-xs text-untitled-gray-500">
          A quick snapshot of your learning activity.
        </p>
      </div>
    </div>

    <div class="lms-stat-grid">
      <template v-if="loadingSummary">
        <div
          v-for="n in 5"
          :key="n"
          class="flex items-center gap-3 rounded-xl border border-untitled-gray-200 bg-white p-4"
        >
          <div
            class="w-11 h-11 rounded-lg bg-untitled-gray-100 animate-pulse shrink-0"
          />
          <div class="flex-1 min-w-0">
            <div class="h-5 w-12 rounded bg-untitled-gray-100 animate-pulse" />
            <div
              class="h-3 w-20 rounded bg-untitled-gray-100 animate-pulse mt-2"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <V4StatCard
          v-for="stat in reportSummaryCards"
          :key="stat.key"
          :stat="stat"
          @on-click-card="handleClickCard"
        />
        <V4CompletedContents />
      </template>
    </div>
    <div v-if="hasViewTeamProgressPermission" class="w-full mt-2">
      <button
        type="button"
        class="w-full flex items-center justify-between gap-2 rounded-lg border border-untitled-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-untitled-gray-700 hover:border-app-primary/40 hover:text-app-primary transition-colors"
        @click="handleClickShowTeamProgress"
      >
        <span class="flex items-center gap-2">
          <UserGroupIcon class="w-4 h-4" />
          {{
            showTeamProgress
              ? "Hide your team's progress"
              : "Show your team's progress"
          }}
        </span>
        <ChevronDownIcon
          class="w-4 h-4 transition-transform"
          :class="{ 'rotate-180': showTeamProgress }"
        />
      </button>
      <V4TeamProgress  v-if="showTeamProgress"/>
      <V4TeamMembers v-if="showTeamProgress" />
    </div>

    <V4CompletedCoursesModal
      v-if="showCompletedCoursesModal"
      v-model="showCompletedCoursesModal"
    />
    <V4CourseInProgressModal
      v-if="showInProgressCoursesModal"
      v-model="showInProgressCoursesModal"
    />
    <V4AssignedCourseModal
      v-if="showAssignedCoursesModal"
      v-model="showAssignedCoursesModal"
    />
    <V4CertificatesEarnedModal
      v-if="showCertificatesEarnedModal"
      v-model="showCertificatesEarnedModal"
    />
  </div>
</template>

<style scoped></style>
